import { productsCategories, productsSlugs } from 'constants/products';

export const pumpSelector = {
  cta: 'Go Green and Reuse Pumps',
  ctaOptedOut: 'No, don’t include pumps in this order',
  ctaOptedIn: 'Yes, include pumps in this order',
  description:
    'Exclude pumps from this shipment and reuse the ones you already have. ' +
    'Every little bit helps!',
};

export const pumpsOptOutCheckboxContent = {
  oneOffOrder: {
    ctaLabel: (checkoutCategory: string) =>
      checkoutCategory === productsCategories.SKINCARE
        ? 'Check this box if you need pumps and droppers added to your order.'
        : 'Check this box if you need pumps added to your order.',
    description: (checkoutCategory: string) =>
      checkoutCategory === productsCategories.SKINCARE
        ? 'Pumps and droppers are not included in this order to reduce waste.'
        : 'Pumps are not included in this order to reduce waste.',
    id: 'pumps-checkbox-one-off-order',
  },
  withSubscription: {
    ctaLabel: (checkoutCategory: string) =>
      checkoutCategory === productsCategories.SKINCARE
        ? 'Check this box if you need pumps and droppers added to your future subscription orders.'
        : 'Check this box if you need pumps added to your future subscription orders.',
    description: (checkoutCategory: string) =>
      checkoutCategory === productsCategories.SKINCARE
        ? 'Only your first subscription order includes pumps and droppers to reduce waste.'
        : 'Only your first subscription order includes pumps to reduce waste.',
    id: 'pumps-checkbox-subscription-order',
  },
  dataTestId: (wantsPumps: boolean) => `pumps-confirmation-${wantsPumps ? 'checked' : 'unchecked'}`,
};

type ClinicalClaim = {
  header: string;
  numbers: string;
  detail?: string;
};

export const clinicalClaimsContent: Record<string, ClinicalClaim> = {
  [productsSlugs.SCALP_SERUM]: {
    header: 'Shown Effective',
    numbers:
      'Helps promote visibly fuller, denser, healthier-looking hair¹, for a healthier scalp and stronger hair in just 4 weeks¹.',
    detail:
      '(1) Results of a 4-week use-test of 97 subjects who tested Prose Custom Scalp Serum every day (agree to strongly agree).',
  },

  [productsSlugs.CLEANSER]: {
    header: 'Clinically tested',
    numbers: '100% say their cleanser left their skin clean¹, 95% say their skin feels soft¹',
    detail:
      '(1) Based on a 4-week clinical study of 22 subjects with sensitive skin aged from 18 to 65 years old who tested Prose Gel Cleanser texture twice a day.',
  },
  [productsSlugs.CONDITIONER]: {
    header: 'Shown Effective',
    numbers: 'Up to 3X stronger hair after 1 use¹, Up to 3X less breakage after 1 use¹',
    detail:
      '(1) instrumental test when used with Prose Custom Shampoo  vs. non-conditioning shampoo',
  },
  [productsSlugs.CURL_CREAM]: {
    header: 'Shown Effective',
    numbers: '24H curl hold¹, 24H frizz control¹',
    detail: '(1) Instrumental test in high humidity conditions',
  },
  [productsSlugs.HAIR_MASK]: {
    header: 'Shown Effective',
    numbers: 'Up to 3X less breakage after 1 use¹, Up to 3X stronger hair after 1 use',
    detail:
      '(1) instrumental test when used with Prose Custom Shampoo & Custom Conditioner  vs. non-conditioning shampoo',
  },
  [productsSlugs.HAIR_OIL]: {
    header: 'Shown Effective',
    numbers:
      '89% said their hair felt softer & smoother¹, 82% noticed beautiful shine to their hair¹',
    detail:
      '(1) Consumer testing panel of 45 testers to whom the oil has been gifted, after minimum 2 weeks of use',
  },
  [productsSlugs.LEAVEIN_CONDITIONER]: {
    header: 'Shown Effective',
    numbers:
      'Up to 450°F heat protection after 1 use¹, Up to  2X stronger hair against heat damage or breakage after 1 use¹',
    detail: '(1) Instrumental test vs. non-treated hair',
  },
  [productsSlugs.MOISTURIZER]: {
    header: 'Clinically tested',
    numbers: '100% say their skin is hydrated³, 92% say their skin looks healthy³',
    detail:
      '(3) Based on results after one use from a 4-week use test of 72 subjects aged from 25 to 70 years old with sensitive skin that tested Prose Moisturizer Cream twice a day.',
  },
  [productsSlugs.OIL]: {
    header: 'Shown Effective',
    numbers: 'Shown effective - 80% saw less frizz¹ using their Prose personalized hair oil',
    detail:
      '(1) Results of 3480 Prose customer’s feedback in the Review & Refine® survey from 2022-01-01 to 2023-02-07',
  },
  [productsSlugs.SCALP_MASK]: {
    header: 'Shown Effective',
    numbers: 'Helps to balance and strengthen the scalp barrier',
  },
  [productsSlugs.SERUM]: {
    header: 'Clinically tested',
    numbers: '91% say their skin is firmer², 82% say wrinkles appearance is reduced²',
    detail:
      '(2) Based on a 4-week use test of 72 subjects aged from 25 to 70 years old with sensitive skin who tested Prose serum twice a day. ',
  },
  [productsSlugs.SHAMPOO]: {
    header: 'Shown Effective',
    numbers:
      'Helps increase hair strength & reduce breakage¹, formulated at low pH to help seal the hair’s outer cuticle & improve fiber health',
    detail:
      '(1) Instrumental test when used with Prose Custom Conditioner vs. non-conditioning shampoo',
  },
  [productsSlugs.SUPPLEMENT_CORE]: {
    header: 'Shown Effective',
    numbers: '93% saw improved hair growth¹, 91% saw reduced hair loss²',
    detail:
      '(1) Results of a 3-month in-house study of Prose 2-step hair growth program performed on 69 subjects. Results may vary. (2) Double blind clinical study vs. placebo related to our proprietary blend performed on 60 subjects during 12 weeks.',
  },
  [productsSlugs.STYLING_GEL]: {
    header: 'Shown Effective',
    numbers: '92% agree their hair was easy to style¹, 91% agree that Styling Gel doesn’t flake¹',
    detail:
      '(1) Results of use test performed on 104 subjects with hair type 2C to 4C after 15 days of use',
  },
};
